<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import StarsImage from '@/assets/images/puzzle-challenge-modal-stars.webp'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import { computed, nextTick, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'
import CrossIcon from '@/components/icons/CrossIcon.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useAuth } from '@/composables/auth'
import { usePuzzleChallengeLeaderboard } from '@/composables/game/leader-board'
import { usePuzzleChallenge } from '@/composables/game/challenges'
import { type CardChallengeLeaderboardPlayer } from '@/types/leaderboard-player'
import { formatUnshortenedNumber } from '@/helpers/utils'
import ChallengeBoardCard2 from '@/components/ChallengeBoardCard2.vue'
import ChallengePrizeItem from '@/components/icons/ChallengePrizeItem.vue'
import PuzzleChallengeScoreImage from '@/assets/images/icons/timer-icon.webp'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { isPrizePoolNft } from '@/types/shared/player-config'
import SummaryCard from '@/components/SummaryCard.vue'
import { usePuzzlesOverlay } from '@/composables/game/puzzles'
import { formatMillisecondDuration } from '@/helpers/date'

const props = defineProps<{
	show: boolean
}>()
const emit = defineEmits(['hide'])

const { profile } = useAuth()
const { openPuzzlesOverlay } = usePuzzlesOverlay()
const { addHapticFeedback } = useHapticFeedback()
const { open } = useTonConnectModal()
const { rawAddress } = useWalletConnect()
const {
  getPuzzleChallengeLeaderboard,
  puzzleChallengeLeaderboard: players,
  currentPlayer,
} = usePuzzleChallengeLeaderboard()

const {
  formattedCountdown,
  winnersCount,
  prizePool,
  getPrizeNameForPosition,
  getPrizeImageForPosition,
  getPrizeForPosition,
  getPrizeValueForPosition,
  prizeTypeImage,
  prizeTypeText,
  getBadgeForPosition,
  getCardMedal,
} = usePuzzleChallenge()

const loading = ref(false)
const showPrizes = ref(false)
const showRules = ref(false)
const container = ref<HTMLElement | null>(null)
const inScrollView = ref(false)
const isScrolled = ref(false)
const maxVisibleRank = ref<number>(100)
const listLimit = ref<number>(100)

const playerForCard = computed<CardChallengeLeaderboardPlayer | undefined>(() => {
  if (currentPlayer.value) {
    return {
      firstName: profile.value.firstName,
      photoUrl: profile.value.photoUrl,
      score: currentPlayer.value.score,
      isThisPlayer: true,
    }
  }
  return undefined

})

function handleScroll() {
  if (container.value) {
    if (currentPlayer.value?.rank) {
      const cardElement = container.value.querySelector(`.card:nth-child(${currentPlayer.value.rank})`) as HTMLElement
      if (cardElement) {
        const cardRect = cardElement.getBoundingClientRect()
        inScrollView.value = cardRect.bottom <= container.value.getBoundingClientRect().bottom
      } else {
        inScrollView.value = false
      }
    }
    isScrolled.value = container.value.scrollTop > 30
  }
}

function onCtaClick() {
  if (!rawAddress.value) {
    open()
    return
  }
  openPuzzlesOverlay()
  emit('hide')
}

watch(() => props.show, async () => {
  if (props.show) {
    loading.value = true
    await getPuzzleChallengeLeaderboard()
    loading.value = false
  }
}, { immediate: true })

watch(rawAddress, async rawAddress => {
  if (!rawAddress) return
  loading.value = true
  await getPuzzleChallengeLeaderboard()
  loading.value = false
})

watch([container, currentPlayer, players], () => {
  inScrollView.value = true
  nextTick(() => {
    setTimeout(() => {
      handleScroll()
    }, 50)
  })
}, { immediate: true })
</script>

<template>
  <TheBottomSheet
    :show="show"
    centered
    :base-modal-class="`green-shadow overflow-visible rounded-[32px] h-[74%] mt-10 ${showRules ? 'h-[45%]' : 'h-[74%]'}`"
    :content-class="`transition-[background] duration-300 overflow-visible pb-0 px-0 pt-[1px] flex flex-col ${showPrizes || showRules ? 'bg-black' : 'bg-[linear-gradient(180deg,_#FF0533_0.01%,_#5A83A7_21.93%,_#000_36.54%)]'}`"
    :outer-content-class="`transition-[background] duration-300 overflow-visible ${showPrizes || showRules ? 'bg-black' : 'bg-[linear-gradient(180deg,_#FF0533_0.01%,_#5A83A7_23.08%,_#000_36.54%)]'}`"
    border-container-class="overflow-visible"
    :back-button="showPrizes || showRules"
    back-button-class="z-[5]"
    @hide="emit('hide')"
    @back="addHapticFeedback('medium'); showPrizes = false; showRules = false"
  >
    <template #closeButton>
      <div
        class="absolute w-6 h-6 text-black top-0 right-0 z-[5] bg-[#FF3737] rounded-full flex items-center justify-center"
        @click="$emit('hide')"
      >
        <CrossIcon class="text-white [&>path]:stroke-[3px] size-[14px]" />
      </div>
    </template>
    <template #baseContent>
      <div
        class="h-0 w-full relative"
        @click="$emit('hide')"
      >
        <img
          class="h-auto w-full absolute top-full left-1/2 -translate-x-1/2 -translate-y-[60%]"
          src="@/assets/images/puzzle-challenge-modal-background.webp"
        >
        <img
          src="@/assets/images/puzzle-challenge-icon.webp"
          class="size-[225px] absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[100px] z-[101] transition-all duration-300"
          :class="isScrolled ? 'scale-[70%]' : 'scale-1'"
        >
        <div
          class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[50px] bg-[#3B9A7C] blur-[27px] rounded-full z-[100] transition-opacity duration-300"
        />
        <img
          :src="StarsImage"
          class="h-[138px] w-auto absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[78px] z-[101]"
        >
      </div>
    </template>
    <div class="pointer-events-none w-[calc(100%-4px)] fixed left-0.5 z-[4] top-0 mt-0.5 rounded-b-[32px] rotate-180" />
    <div
      ref="container"
      @scroll="handleScroll"
      class="w-full overflow-auto scrollable-container flex flex-col flex-1 relative h-full"
    >
      <div
        class="px-6 flex flex-col w-full h-full items-center text-center pt-[80px]"
      >
        <h2 class="whitespace-nowrap text-light-white text-[36px] leading-9 font-bold mb-2">
          <T
            v-if="showPrizes"
            key-name="daily_up_challenge.modal.prizes_title"
            default-value="Prizes"
          />
          <T
            v-else
            key-name="daily_up_challenge.modal.title"
            default-value="Jigsaw challenge"
          />
        </h2>
        <p
          class="font-medium text-base leading-5 -tracking-[0.32px] mb-6 whitespace-pre-line"
          :class="showPrizes || showRules ? 'text-light-white' : 'text-light-white/70'"
        >
          <template v-if="showPrizes">
            <T
              key-name="daily_up_challenge.modal.prizes_description1"
              default-value="Prizes will be sent shortly after the end of the challenge. Make sure to "
            />
            <span
              :class="{'pointer-events-none': !!rawAddress, 'border-b border-green-primary text-green-primary': !rawAddress}"
              @click="open"
            >
              <T
                key-name="general.connect"
                default-value="connect"
              />
            </span>
            <T
              key-name="daily_up_challenge.modal.description2"
              default-value=" your wallet to receive your prize!"
            />
          </template>
          <template v-else-if="showRules">
            <div class="flex flex-col items-center gap-y-3">
              <span>How it works:</span>
              <span>
                • Reveal the image behind the
                <span
                  :class="{'pointer-events-none': !!currentPlayer?.score, 'border-b border-current': !currentPlayer?.score}"
                  @click="openPuzzlesOverlay"
                >
                  jigsaw puzzle
                </span>
                as quickly as you can to climb the leaderboard
              </span>
              <span>
                • Make sure to
                <span
                  :class="{'pointer-events-none': !!rawAddress, 'border-b border-current': !rawAddress}"
                  @click="open"
                >
                  connect
                </span>
                your wallet to participate.
              </span>
            </div>
          </template>
          <template v-else>
            <T
              key-name="daily_up_challenge.modal.description1"
              default-value="Be the fastest to complete the jigsaw and claim rewards! "
            />
            <span
              :class="{'pointer-events-none': !!rawAddress, 'border-b border-current': !rawAddress}"
              @click="open"
            >
              <T
                key-name="general.connect"
                default-value="Connect"
              />
            </span>
            <T
              key-name="daily_up_challenge.modal.description2"
              default-value=" your wallet to join the race."
            />
          </template>
        </p>

        <div
          v-if="showPrizes && prizePool"
          class="w-full flex-1 flex flex-col pb-6"
        >
          <ChallengePrizeItem
            v-for="(prizeItem, ind) in prizePool.amounts"
            :key="ind"
            :prize-type="prizePool.type"
            :prize-item="prizeItem"
            :prize-image="getPrizeImageForPosition(ind+1)!"
          />
        </div>
        <div
          v-else
          class="flex-1 w-full flex flex-col items-center"
        >
          <BaseButton
            v-if="!showPrizes"
            class="mb-6 w-full"
            button-class="border-0 h-full py-4 px-6 h-[60px]"
            :disabled="(currentPlayer?.score ?? 0) > 0"
            @click="onCtaClick"
          >
            <div class="flex items-center gap-x-2">
              <T
                v-if="currentPlayer?.score"
                key-name="challenge.modal.puzzleCracked"
                default-value="Jigsaw Cracked"
              />
              <T
                v-else-if="!rawAddress"
                key-name="wallet.connect_wallet"
                default-value="Connect wallet"
              />
              <T
                v-else
                key-name="challenge.modal.cta"
                default-value="Crack Jigsaw Puzzle"
              />
              <ArrowRightIcon class="size-6 [&>path]:stroke-2" />
            </div>
          </BaseButton>
          <template v-if="!showRules">
            <div class="w-full flex gap-x-4">
              <SummaryCard
                content-class="pt-3"
                top-text="Total Prizes"
                :middle-image="prizeTypeImage"
                :middle-text="prizeTypeText"
                :bottom-text="`${winnersCount} WINNERS`"
                @click="showPrizes = true"
              />
              <SummaryCard
                background-class="w-1/2"
                border-class="bg-white/[.38]"
                top-text="Rule"
                middle-text="Reveal"
                bottom-text="Image"
                top-class="text-lg font-semibold leading-[18px] text-light-white"
                middle-class="whitespace-pre-line text-center text-xl font-bold leading-6 text-indiana-dark"
                bottom-class="whitespace-pre-line text-center text-xl font-bold leading-6 text-indiana-dark"
                @click="showRules = true"
              />
            </div>

            <div class="mt-6 -mx-6 h-7 w-[calc(100%+48px)] bg-red-countdown-banner flex items-center justify-center">
              <div class="uppercase text-white text-base font-bold text-shadow-default pr-1.5">
                Ends in
              </div>
              <img
                class="size-[18px] object-cover"
                src="@/assets/images/gold-clock.png"
              >
              <div class="ml-1 golden-text-black-stroke text-base font-bold gold-text-shadow px-0.5">
                {{ formattedCountdown }}
              </div>
            </div>

            <div
              v-if="!loading"
              :class="twMerge('w-full flex flex-col relative', !players.length && 'flex-1 items-center justify-center  h-full')"
            >
              <div
                v-if="players.length"
                class="mt-6 flex flex-col w-full gap-y-2 pb-4 items-center"
              >
                <ChallengeBoardCard2
                  v-for="(leadingBoardPlayer, index) in players.slice(0, maxVisibleRank)"
                  :key="index"
                  :player="leadingBoardPlayer"
                  :index="(index+1).toString()"
                  :is-own="leadingBoardPlayer.isThisPlayer"
                  :medal="getCardMedal(index+1)"
                  :prize-value="getPrizeValueForPosition(index+1)"
                  :prize-image="getPrizeImageForPosition(index+1)"
                  :prize-name="getPrizeNameForPosition(index+1)"
                  :score-image="PuzzleChallengeScoreImage"
                  :score-value="formatMillisecondDuration(leadingBoardPlayer.score!)"
                  :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(index+1))"
                  :badge="getBadgeForPosition(index+1)"
                  class="card"
                />
                <p
                  v-if="listLimit <= players.length"
                  class="mt-3 mb-4 text-center max-w-[314px] text-base font-medium text-white/60"
                  :class="currentPlayer && currentPlayer.rank > players.length && 'pb-10'"
                >
                  <T
                    key-name="levels.many_more"
                    default-value="And many more players who didn’t make it into the top {limit}... yet."
                    :params="{limit: Math.min(players.length, listLimit)}"
                  />
                </p>
                <ChallengeBoardCard2
                  v-else-if="players.length > 1"
                  :player="playerForCard"
                  :index="`${(maxVisibleRank).toString()}+`"
                  :prize-value="getPrizeValueForPosition(maxVisibleRank)"
                  :prize-image="getPrizeImageForPosition(maxVisibleRank)"
                  :prize-name="getPrizeNameForPosition(maxVisibleRank+1)"
                  :score-image="PuzzleChallengeScoreImage"
                  :score-value="playerForCard && formatMillisecondDuration(playerForCard.score!)"
                  :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(maxVisibleRank+1))"
                  :badge="getBadgeForPosition(maxVisibleRank)"
                  card-class="invisible"
                />
              </div>
              <div
                v-else
                class="flex flex-1 flex-col items-center justify-center w-full text-center py-6"
              >
                <h2 class="text-lg -tracking-[0.36px] font-bold text-light-white">
                  <T
                    key-name="card-challenge.empty.title"
                    default-value="Nothing to see here yet"
                  />
                </h2>
                <p class="text-sm font-medium tracking-tight text-white/[.87]">
                  <T
                    key-name="card-challenge.empty.description"
                    default-value="Be the first to join the challenge"
                  />
                </p>
              </div>
              <div
                v-if="playerForCard && currentPlayer && !inScrollView"
                class="w-full fixed bottom-4 z-[900] left-1/2 -translate-x-1/2 px-4"
              >
                <ChallengeBoardCard2
                  :player="playerForCard"
                  :index="formatUnshortenedNumber(currentPlayer.rank)"
                  :medal="getCardMedal(currentPlayer.rank)"
                  :prize-value="getPrizeValueForPosition(currentPlayer.rank)"
                  :prize-image="getPrizeImageForPosition(currentPlayer.rank)"
                  :prize-name="getPrizeNameForPosition(currentPlayer.rank)"
                  :score-image="PuzzleChallengeScoreImage"
                  :score-value="playerForCard && formatMillisecondDuration(playerForCard.score!)"
                  :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(currentPlayer.rank))"
                  :badge="getBadgeForPosition(currentPlayer.rank)"
                  is-own
                  card-class="border-green-primary"
                  rank-wrapper-class="min-w-fit mr-1"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
.scale-up {
	animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.03);
	}
}

.bg-green-gradient-stroke {
	background: linear-gradient(
			-45deg,
			rgba(237, 255, 202, 0.22) 13%,
			#48650c 24%,
			#3B9A7C 42%,
			#3B9A7C 63%,
			rgba(238, 255, 202, 0.77) 79%,
			#e2ffa7 82%
	);
	box-shadow: 0px 0px 24px 0px rgba(230, 185, 12, 0.66);
}

.scrollable-container {
	-ms-overflow-style: none; /* Internet Explorer 10+ */
	scrollbar-width: none; /* Firefox */
}

.scrollable-container::-webkit-scrollbar {
	display: none; /* Safari and Chrome */
}

.text-clip-green-gradient {
	background: var(--Blue-Degrade, linear-gradient(180deg, #E2FFA5 0%, #C6F36B 98%));
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}
</style>

<style lang="scss">
@import "@/assets/styles/challenges";

.text-shadow-default {
	text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}

.gradient-black-fade {
	background: linear-gradient(
			to top,
			rgba(0, 0, 0, 1) 42.5%,
			rgba(0, 0, 0, 0.65) 65.15%,
			rgba(0, 0, 0, 0) 100%
	);
}
</style>